import { NEW_TUTOR_ONBOARDING_TOPIC } from '@/queries/constants'
import {
  getCertificationUrl,
  getDashboardUrl,
  getReferUrl,
  getTutorAuditUrl,
  getUrl,
} from '@/client/services/url'
import { getArticleUrl } from '@/client/services/url/faq'

import { LeaderboardKey, type LeaderboardPanelInfo, LeaderboardTimeFrame } from './types'

export const LEADERBOARD_LIMIT = 50
export const LEADERBOARD_PAGE_LIMIT = 10

export const VISIBLE_LEADERBOARD_KEYS = [
  LeaderboardKey.PointsLearners,
  LeaderboardKey.PointsTutors,
  LeaderboardKey.QuestionsAnswered,
  LeaderboardKey.Certifications,
  LeaderboardKey.SessionsAttended,
  LeaderboardKey.SessionsHosted,
  LeaderboardKey.SessionsAudited,
  LeaderboardKey.SuperHelpfuls,
  LeaderboardKey.LiveHelpHours,
  LeaderboardKey.LearnersReferred,
  LeaderboardKey.TutorsReferred,
  LeaderboardKey.Badges,
]

export const WORLD_LEADERBOARD_KEYS = [
  LeaderboardKey.QuestionsAnswered,
  LeaderboardKey.SessionsAttended,
  LeaderboardKey.SessionsHosted,
  LeaderboardKey.SessionsAudited,
]

export const LEADERBOARD_TIME_FRAMES = [
  LeaderboardTimeFrame.Week,
  LeaderboardTimeFrame.Month,
  LeaderboardTimeFrame.AllTime,
]

export const LEADERBOARD_TIME_FRAME_TO_LABEL: Record<LeaderboardTimeFrame, string> = {
  [LeaderboardTimeFrame.AllTime]: 'All time',
  [LeaderboardTimeFrame.Week]: 'This week',
  [LeaderboardTimeFrame.Month]: 'This month',
}

export const LEADERBOARD_KEY_TO_PANEL_INFO: Record<LeaderboardKey, LeaderboardPanelInfo> = {
  [LeaderboardKey.LiveHelpHours]: {
    title: 'Most Live Help Hours Hosted',
    action: 'Host a Live Help session',
    url: getDashboardUrl('live-help'),
    unit: 'hour',
  },
  [LeaderboardKey.PointsLearners]: {
    title: 'Most SP (Learners)',
    action: 'Get some SP',
    url: getArticleUrl('sp-and-badges'),
    unit: 'SP',
    customPluralUnit: 'SP',
  },
  [LeaderboardKey.CertificationsReviewed]: {
    title: 'Most Certifications Reviewed',
    action: 'Review a certification',
    url: getCertificationUrl('review'),
    unit: 'certification',
  },
  [LeaderboardKey.PointsTutors]: {
    title: 'Most SP (Tutors)',
    action: 'Get some SP',
    url: getArticleUrl('sp-and-badges'),
    unit: 'SP',
    customPluralUnit: 'SP',
  },
  [LeaderboardKey.Badges]: {
    title: 'Most Badges',
    action: 'Get a badge',
    url: getArticleUrl('sp-and-badges'),
    unit: 'badge',
  },
  [LeaderboardKey.Certifications]: {
    title: 'Most Certifications',
    action: 'Get a certification',
    url: getCertificationUrl('get-certified'),
    unit: 'certification',
  },
  [LeaderboardKey.LearnersReferred]: {
    title: 'Most Learners Referred',
    action: 'Refer a learner',
    url: getReferUrl(),
    unit: 'learner',
  },
  [LeaderboardKey.TutorsReferred]: {
    title: 'Most Tutors Referred',
    action: 'Refer a tutor',
    url: getReferUrl(),
    unit: 'tutor',
  },
  [LeaderboardKey.SessionsAttended]: {
    title: 'Most Sessions Attended',
    action: 'Attend a session',
    url: getDashboardUrl('sessions'),
    unit: 'session',
  },
  [LeaderboardKey.SessionsHosted]: {
    title: 'Most Sessions Hosted',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'session',
  },
  [LeaderboardKey.SuperHelpfuls]: {
    title: 'Most "Super Helpful" Ratings',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'rating',
  },
  [LeaderboardKey.SessionsAudited]: {
    title: 'Most Sessions Reviewed',
    action: 'Peer Review a session',
    url: getTutorAuditUrl(),
    unit: 'review',
  },
  [LeaderboardKey.TutorsWelcomed]: {
    title: 'Most New Tutors Onboarded',
    action: `Host a ${NEW_TUTOR_ONBOARDING_TOPIC} session`,
    url: getUrl('tutor'),
    unit: 'tutor',
  },
  [LeaderboardKey.QuestionsAnswered]: {
    title: 'Most Questions Answered',
    action: 'Answer a question',
    url: getDashboardUrl('live-help'),
    unit: 'question',
  },
  [LeaderboardKey.TutoringHours]: {
    title: 'Most Hours Tutored',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'hour',
  },
  [LeaderboardKey.CountriesReached]: {
    title: 'Most Countries Reached',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'country',
    customPluralUnit: 'countries',
  },
  [LeaderboardKey.LearnersImpacted]: {
    title: 'Most Learners Impacted',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'learner',
  },
  [LeaderboardKey.PositiveRatings]: {
    title: 'Most Positive Ratings',
    action: 'Host a session',
    url: getUrl('tutor'),
    unit: 'rating',
  },
}
